import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import type {
  OasisValidationError,
  TaskStatus,
} from '../../services/api/types.gen';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { CmsIssueCard } from './CmsIssueCard';
import {
  chartFixTargetForError,
  cmsItemHome,
  type CmsItemHome,
} from './cmsFixTarget';

/** Fixed in Patient Info or clinic settings, which a locked chart does not block. */
const OUTSIDE_CHART = new Set<CmsItemHome>(['patient', 'clinic']);

interface OasisExportErrorsDialogProps {
  open: boolean;
  errors: OasisValidationError[];
  questionTextByQid: Record<string, string>;
  /** What to do about them, for whoever is looking. */
  hint: string;
  onClose: () => void;
  /** What the dialog explains: errors that hold up the iQIES export, a record
   * the nightly run cannot send SHP, or errors an SHP check stopped on. */
  holds?: 'export' | 'shp' | 'shp_check';
  /** The chart's status; a locked one is said to need reopening first. */
  chartStatus?: TaskStatus;
  /** Why SHP could not file the CMS record, one problem each. */
  recordProblems?: string[];
  /** The way to the fix: "Open chart" from the OASIS Manager, "Reopen" on it. */
  actionLabel?: string;
  onAction?: () => void;
  /** Opens the place to fix one error; without it the cards are read-only. */
  onJump?: (error: OasisValidationError) => void;
  /** False for an error with nowhere to go: its card stays read-only. */
  canJump?: (error: OasisValidationError) => boolean;
}

const LOCKED_WORD: Partial<Record<TaskStatus, string>> = {
  completed: 'Completed',
  exported: 'Exported',
};

/**
 * Why a completed OASIS chart cannot be exported, or cannot be checked with
 * SHP: the All CMS scrubber's fatal errors, which CMS rejects on,
 * as the All CMS tab's own cards. For a record SHP could not file, its problems.
 */
export const OasisExportErrorsDialog: React.FC<
  OasisExportErrorsDialogProps
> = ({
  open,
  errors,
  questionTextByQid,
  hint,
  onClose,
  holds = 'export',
  chartStatus,
  recordProblems = [],
  actionLabel,
  onAction,
  onJump,
  canJump = () => true,
}) => {
  const count = `${errors.length} ${errors.length === 1 ? 'error' : 'errors'}`;
  const locked =
    chartStatus !== undefined && !EDITABLE_STATUSES.has(chartStatus);
  const lockedWord = (chartStatus && LOCKED_WORD[chartStatus]) ?? 'locked';
  const fixedOutsideChart = errors.some(error =>
    (error.oasis_fields ?? []).some(field =>
      OUTSIDE_CHART.has(cmsItemHome(field))
    )
  );
  const fixedInChart = errors.some(
    error => chartFixTargetForError(error) !== null
  );
  // Held because SHP could not file the record: no CMS error card to click.
  const recordOnly = errors.length === 0 && recordProblems.length > 0;
  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className='sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <AlertTriangle className='h-5 w-5 shrink-0 text-amber-600' />
            {holds === 'shp'
              ? "SHP can't file this chart"
              : holds === 'shp_check'
                ? 'Fix CMS errors before an SHP check'
                : "This chart can't be exported"}
          </DialogTitle>
          <DialogDescription className='space-y-2 pt-2 text-sm'>
            <span className='block'>
              {recordOnly
                ? 'The CMS record built from this chart is missing what SHP files it by.'
                : `This chart has ${count} that CMS will reject.`}
              {holds === 'shp' &&
                recordOnly &&
                ' It goes to SHP on the first night after the record is fixed.'}
            </span>
            {locked && recordOnly && (
              <span className='block font-medium text-foreground'>
                This chart is {lockedWord}, so it has to be reopened to fix
                chart answers.
              </span>
            )}
            {recordOnly && (
              <span className='block'>
                A problem no chart answer explains is in how Roger builds the
                record: report it to Roger support.
              </span>
            )}
            {locked && fixedInChart && (
              <span className='block font-medium text-foreground'>
                This chart is {lockedWord}, so it has to be reopened to fix
                chart answers.
                {fixedOutsideChart &&
                  ' Patient Info and clinic settings are fixed without reopening.'}
              </span>
            )}
            {locked && !fixedInChart && fixedOutsideChart && (
              <span className='block font-medium text-foreground'>
                These are fixed in Patient Info or clinic settings. The chart
                does not need to be reopened.
              </span>
            )}
            {!recordOnly && <span className='block'>{hint}</span>}
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] space-y-2 overflow-auto'>
          {recordProblems.length > 0 && (
            <div className='rounded-md border p-3 text-sm'>
              <p className='font-medium text-foreground'>
                Problems with the CMS record
              </p>
              <ul className='mt-1 list-disc space-y-1 pl-5'>
                {recordProblems.map(problem => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}
          {errors.map((error, i) => (
            // A fixed list per render, and one edit can repeat across items.
            <CmsIssueCard
              key={`${error.edit_id}-${i}`}
              error={error}
              qidToQuestionText={questionTextByQid}
              onJump={onJump && canJump(error) ? onJump : undefined}
            />
          ))}
        </div>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Close
          </Button>
          {actionLabel && onAction && (
            <Button type='button' onClick={onAction}>
              {actionLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
